import {
  UnitTestLoaderParameters,
  UnitTestSchema,
  WithRunnerData,
} from '../UnitTestSchema';
import {EMPTY_TEST_RESULT, UnitTestResults} from '../unitTestResults';
import {getGraphicsUnitTestRef} from '../../models/unitTests/graphics';
import {getDoc, setDoc} from 'firebase/firestore';
import {GraphicsEditor} from './GraphicsEditor';
import {
  DEFAULT_GRAPHICS_UNIT_TEST_NAME,
  type LoadedGraphicsUnitTestData,
} from './types';
import {constructFilesListWithContent, getAllFileNames} from 'ide/utils/general';
import {
  areShapeMapsEqual,
  isGraphicsShape,
  type GraphicsShape,
  type ShapeId,
} from '@cip/code-runner';
import type {TranslationFunction} from 'components/translation/utils';

async function loader({
  courseId,
  assignmentId,
  serverAssignmentData,
}: UnitTestLoaderParameters): Promise<LoadedGraphicsUnitTestData | null> {
  const graphicsUnitTestReference = getGraphicsUnitTestRef(
    courseId,
    assignmentId,
  );
  const graphicsUnitTestDoc = await getDoc(graphicsUnitTestReference);
  if (!graphicsUnitTestDoc.exists()) {
    await setDoc(graphicsUnitTestReference, []);
  }
  const unitTests = graphicsUnitTestDoc.exists()
    ? graphicsUnitTestDoc.data()
    : [];
  const starterCode = serverAssignmentData.starterCode ?? {'main.py': ''};
  return {
    unitTests,
    starterCode,
  };
}

async function runner({
  filesCode,
  pyodideClient,
  fileStructure,
  loadedData,
}: WithRunnerData<LoadedGraphicsUnitTestData>): Promise<UnitTestResults> {
  const {unitTests} = loadedData ?? {unitTests: []};

  // Make sure that the unitTests are valid
  if (!unitTests || unitTests.length === 0) {
    return EMPTY_TEST_RESULT;
  }

  const allFileNames = getAllFileNames(fileStructure);
  const mainFile = allFileNames.find(file => file.name === 'main.py');
  if (!mainFile) {
    return {
      testResults: [
        {
          isSuccess: false,
          name: DEFAULT_GRAPHICS_UNIT_TEST_NAME,
          usedAi: false,
          rawResult: {message: 'No main.py file found'},
        },
      ],
      testsRun: true,
    };
  }
  const codeToRun = filesCode?.[mainFile.id]?.content ?? '';
  if (codeToRun === '') {
    return {
      testResults: [
        {
          isSuccess: false,
          name: DEFAULT_GRAPHICS_UNIT_TEST_NAME,
          usedAi: false,
          rawResult: {message: 'No code to run'},
        },
      ],
      testsRun: true,
    };
  }
  const unitTest = unitTests[0];
  const studentCodeResult = await pyodideClient.runCode(codeToRun, {
    files: constructFilesListWithContent(fileStructure, filesCode),
    stepMode: true,
    testMode: 'simple',
    ioOverrides: {
      input: async () => {
        return '';
      },
      // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
      output: async (output: string) => {},
      // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
      error: async (error: string) => {},
      onRunEnd: async () => {}
    }
  });
  if ((studentCodeResult.error?.length ?? 0) > 0) {
    return {
      testResults: [
        {
          isSuccess: false,
          name: DEFAULT_GRAPHICS_UNIT_TEST_NAME,
          usedAi: false,
          rawResult: studentCodeResult,
        },
      ],
      testsRun: true,
    };
  }
  let lastGraphicsData;
  try {
    if (! studentCodeResult?.traceData || studentCodeResult.traceData.length === 0) {
      throw new Error("No trace data found");
    }
    const lastGraphicsString = studentCodeResult.traceData.slice(-1)[0]["canvas_data"] || '{}';
    lastGraphicsData = JSON.parse(lastGraphicsString);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  } catch (e) {
    lastGraphicsData = {};
  }

      
  const studentCanvasData = new Map<ShapeId, GraphicsShape>(
    Object.entries(lastGraphicsData)
      .filter(([, value]) => isGraphicsShape(value))
      .filter(([, value]) => isGraphicsShape(value))
      .map(([key, value]) => [key, value as GraphicsShape] as const),
  );
  const solutionCanvasData = unitTest.map;

  if (
    !areShapeMapsEqual(
      studentCanvasData,
      solutionCanvasData,
      unitTest.compareColors,
    )
  ) {
    return {
      testResults: [
        {
          isSuccess: false,
          name: DEFAULT_GRAPHICS_UNIT_TEST_NAME,
          usedAi: false,
          rawResult: {
            message: `Your canvas and the solution canvas don't match up, keep trying!`,
          },
        },
      ],
      testsRun: true,
    };
  }

  return {
    testResults: [
      {
        isSuccess: true,
        name: DEFAULT_GRAPHICS_UNIT_TEST_NAME,
        usedAi: false,
        rawResult: {
          message: 'Your answer is correct!',
        },
      },
    ],
    testsRun: true,
  };
}

function getFailureMessage(
  unitTestResults: UnitTestResults,
  t: TranslationFunction,
) {
  const firstTestSuccess = unitTestResults.testResults[0].isSuccess;
  if (firstTestSuccess) {
    return '';
  }
  return t(unitTestResults.testResults[0].rawResult.message) as string;
}

export const GRAPHICS_UNIT_TEST_SCHEMA: UnitTestSchema<LoadedGraphicsUnitTestData> =
  {
    loader,
    runner,
    EditorComponent: GraphicsEditor,
    unitTestType: 'graphics',
    displayName: 'Graphics Canvas Match',
    tooltipDescription:
      'Compares the graphics canvas state between the student code and the solution.',
    getFailureMessage,
  };
